import { CLASS_NAME } from '../../../constants/class-names'
import { EventType, KeyType } from 'constants/enums'
import { Nullable } from 'constants/types'
import { HeaderOffsetService } from 'services/header/header-offset.service'
import { ComponentBase } from 'utils/abstract-classes/component-base'
import { SetAriaExpanded } from 'utils/aria'
import { CSSUtils } from 'utils/css'
import { IShareData, nativeAPIShare } from 'utils/web-api/navigator-share'
import { ShareButtonStore } from './store'

const { shareButton: classNames } = CLASS_NAME

export class ShareButtonComponent extends ComponentBase {
    public data: IShareData = {
        title: '',
        url: window.location.href
    }
    // eslint-disable-next-line no-magic-numbers
    private readonly scrollTimeout = 100
    private readonly button: Nullable<HTMLButtonElement>
    private readonly modal: Nullable<HTMLDivElement>
    private readonly close: Nullable<HTMLButtonElement>
    private readonly title: Nullable<HTMLHeadingElement>
    private readonly lastItem: Nullable<HTMLElement>

    /**
     * Initializes the share button.
     * @param {HTMLElement} el
     */
    public constructor(container: HTMLElement) {
        super()

        ShareButtonStore.getInstance().add(this)

        this.button = this.getElement<HTMLButtonElement>(
            `.${classNames._}`,
            container
        )
        this.modal = this.getElement(`.${classNames.modal}`, container)
        this.close = this.getElement(`.${classNames.modalClose}`, container)
        this.lastItem = this.getElement(
            `.${classNames.modalItem}:last-child`,
            container
        )
        this.title = this.getElement(`.${classNames.modalTitle}`, container)

        this.data.title = this.title ? this.title.textContent || '' : ''

        // * attach events
        // Open close modal
        if (this.button) {
            this.button.addEventListener(
                EventType.CLICK,
                async (event: Event) => {
                    // stop default behaviour to check for Web Share Api
                    event.stopPropagation()
                    event.preventDefault()
                    this.tryNativeShare().catch((e) => console.error(e))
                }
            )
        }

        // Close button
        if (this.close) {
            this.close.addEventListener(EventType.CLICK, () => {
                this.closeModal()
            })
        }

        // close modal on tab away from last item
        if (this.lastItem) {
            this.lastItem.addEventListener(EventType.FOCUS_OUT, () =>
                this.closeModal()
            )
        }

        // Close modal on ESC
        document.addEventListener(EventType.KEYDOWN, (e: KeyboardEvent) => {
            if (e.key === KeyType.ESC || e.key === KeyType.ESC_IE) {
                this.closeModal()
            }
        })

        // * when on the non-full screen version of the share modal
        // scrolling should close the modal
        // reduce the amount this gets fired with timeout threshold
        let isScrolling: NodeJS.Timeout
        document.addEventListener('scroll', () => {
            // Clear our timeout throughout the scroll
            window.clearTimeout(isScrolling)

            // Set a timeout to run after scrolling ends
            isScrolling = setTimeout(() => {
                // Close modal if open on Scroll
                if (CSSUtils.checkClass(this.modal, classNames.modalIsOpen)) {
                    this.closeModal()
                }
            }, this.scrollTimeout)
        })

        // Close modal on click outside modal
        window.addEventListener(EventType.CLICK, (e: Event) => {
            const target = e.target as Element
            if (target && this.modal && this.modal.contains(target)) {
                return
            }
            this.closeModal()
        })
    }

    public async tryNativeShare(): Promise<void> {
        // Web Share Api, allowed to try opening native sharing menus
        const nativeShareExists = await nativeAPIShare(this.data)
        // otherwise open the modal
        if (!nativeShareExists) {
            ShareButtonStore.getInstance().close()
            this.openModal()
        }
    }

    public openModal(): void {
        // offsets share model from top of the viewport
        if (this.modal) {
            new HeaderOffsetService(this.modal)
        }

        CSSUtils.addClass(this.modal, classNames.modalIsOpen)
        SetAriaExpanded(this.button, 'true')
    }

    public closeModal(): void {
        CSSUtils.removeClass(this.modal, classNames.modalIsOpen)
        SetAriaExpanded(this.button, 'false')
    }
}
